import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import EnquiryModal from '../components/EnquiryModal';
import ReviewsList from '../components/ReviewsList';
import { Button } from '../components/ui/button';
import { useData } from '../context/DataContext';
import { api, inr } from '../lib/api';
import { ChevronRight, ShieldCheck, MapPin, Star, Phone, MessageSquare, Truck, Award, Package, Check } from 'lucide-react';
import { recent } from '../lib/storage';
import useSEO from '../lib/useSEO';
import ShareButton from '../components/ShareButton';

const ProductDetailPage = () => {
  const { id } = useParams();
  const { tree, supplierById } = useData();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [selectedImg, setSelectedImg] = useState(0);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null); setRelated([]); setSelectedImg(0); setNotFound(false);
    api.getProduct(id).then(async (p) => {
      setProduct(p);
      recent.track(p.id);
      try {
        const rel = await api.getProducts({ sub_category_id: p.sub_category_id, limit: 8 });
        setRelated((rel.items || []).filter((x) => x.id !== p.id));
      } catch (err) { console.warn('ProductDetailPage: failed to load related products', err); }
    }).catch(() => setNotFound(true));
  }, [id]);

  const jsonLd = useMemo(() => {
    if (!product) return null;
    const sup = supplierById?.[product.supplier_id];
    const origin = (typeof window !== 'undefined' && window.location.origin) || '';
    const productUrl = `${origin}/product/${product.slug || product.id}`;
    const productSchema = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name: product.name,
      image: product.images?.length ? product.images : [product.image],
      description: product.description || product.name,
      sku: product.id,
      mpn: product.id,
      url: productUrl,
      brand: { '@type': 'Brand', name: sup?.name || 'TelanganaMart' },
      category: product.category_name || product.sub_category_name || undefined,
      offers: {
        '@type': 'Offer',
        priceCurrency: 'INR',
        price: product.price,
        availability: 'https://schema.org/InStock',
        url: productUrl,
        seller: sup ? {
          '@type': 'Organization',
          name: sup.name,
          address: sup.city ? { '@type': 'PostalAddress', addressLocality: sup.city, addressRegion: 'Telangana', addressCountry: 'IN' } : undefined,
        } : undefined,
      },
      aggregateRating: (sup?.rating && sup.rating > 0)
        ? { '@type': 'AggregateRating', ratingValue: sup.rating, bestRating: 5, reviewCount: 1 }
        : undefined,
    };
    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` },
        { '@type': 'ListItem', position: 2, name: 'Products', item: `${origin}/search` },
        { '@type': 'ListItem', position: 3, name: product.name, item: productUrl },
      ],
    };
    return [productSchema, breadcrumb];
  }, [product, supplierById]);

  useSEO({
    title: product ? `${product.name}${product.unit ? ` — ${inr(product.price)} / ${product.unit}` : ''}` : undefined,
    description: product
      ? (
          (product.description || '').slice(0, 160) ||
          `Buy ${product.name} at ${inr(product.price)} per ${product.unit} from verified Telangana suppliers. Get quotes, specs, MOQ ${product.moq || ''} on TelanganaMart.`
        )
      : undefined,
    keywords: product
      ? [product.name, product.brand, product.unit, supplierById?.[product.supplier_id]?.name, supplierById?.[product.supplier_id]?.city, 'B2B', 'TelanganaMart']
          .filter(Boolean).join(', ')
      : undefined,
    image: product?.image,
    jsonLd,
  });

  if (notFound) {
    return (
      <div>
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold">Product not found</h1>
          <Link to="/" className="text-[#2c54b2] hover:underline mt-4 inline-block">Go back home</Link>
        </div>
        <Footer />
      </div>
    );
  }
  if (!product) {
    return <div><Header /><div className="max-w-7xl mx-auto px-4 py-20 text-center text-gray-500">Loading product...</div><Footer /></div>;
  }

  const supplier = supplierById[product.supplier_id];
  // resolve hierarchy from tree
  let sub = null, cat = null, main = null;
  for (const m of tree) {
    for (const c of (m.categories || [])) {
      const f = (c.sub_categories || []).find((s) => s.id === product.sub_category_id);
      if (f) { sub = f; cat = c; main = m; break; }
    }
    if (sub) break;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-1 text-xs text-gray-600 flex-wrap">
          <Link to="/" className="hover:text-[#2c54b2]">Home</Link>
          <ChevronRight className="w-3 h-3" />
          {main && <><Link to={`/main/${main.slug}`} className="hover:text-[#2c54b2]">{main.name}</Link><ChevronRight className="w-3 h-3" /></>}
          {cat && <><Link to={`/category/${cat.slug}`} className="hover:text-[#2c54b2]">{cat.name}</Link><ChevronRight className="w-3 h-3" /></>}
          {sub && <><Link to={`/subcategory/${sub.slug}`} className="hover:text-[#2c54b2]">{sub.name}</Link><ChevronRight className="w-3 h-3" /></>}
          <span className="text-gray-900 font-medium truncate">{product.name}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-[1fr_1fr_320px] gap-6">
        <div>
          <div className="bg-white border border-gray-200 rounded-md overflow-hidden aspect-square">
            <img src={product.images[selectedImg]} alt={product.name} className="w-full h-full object-cover" />
          </div>
          {product.images.length > 1 && (
            <div className="flex gap-2 mt-3">
              {product.images.map((img, i) => (
                <button key={`thumb-${img}-${i}`} onClick={() => setSelectedImg(i)} className={`w-16 h-16 rounded border-2 overflow-hidden ${selectedImg === i ? 'border-[#ee5e23]' : 'border-gray-200'}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-start justify-between gap-3">
            <h1 className="text-2xl font-bold text-gray-900 flex-1">{product.name}</h1>
            <ShareButton
              title={product.name}
              text={`${product.name} at ${inr(product.price)} / ${product.unit} on TelanganaMart`}
              url={`${typeof window !== 'undefined' ? window.location.origin : ''}/api/og/product/${product.id}`}
              testid="product-share"
            />
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className="flex items-center gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => <Star key={`pd-star-${i}`} className="w-4 h-4 fill-amber-500 text-amber-500" />)}
            </span>
            <span className="text-sm text-gray-600">(4.5) 128 Reviews</span>
          </div>
          <div className="flex items-baseline gap-2 mt-4">
            <span className="text-3xl font-bold text-[#2c54b2]">{inr(product.price)}</span>
            <span className="text-sm text-gray-600">/ {product.unit}</span>
            <span className="text-xs ml-2 text-green-700 font-medium">Get Latest Price</span>
          </div>
          <div className="mt-1 text-sm text-gray-700">Minimum Order Quantity: <span className="font-semibold">{product.moq}</span></div>

          <div className="grid grid-cols-2 gap-3 mt-5">
            {Object.entries(product.specs || {}).map(([k, v]) => (
              <div key={k} className="text-sm">
                <div className="text-xs text-gray-500">{k}</div>
                <div className="font-medium text-gray-900">{v}</div>
              </div>
            ))}
          </div>

          <div className="mt-5 border-t border-gray-200 pt-4">
            <h3 className="font-semibold text-gray-900 mb-2">Product Description</h3>
            <p className="text-sm text-gray-700 leading-relaxed">{product.description}</p>
          </div>

          <div className="mt-5 grid grid-cols-3 gap-3">
            {[
              { key: 'delivery', icon: Truck, t: 'Pan Telangana Delivery' },
              { key: 'quality', icon: Award, t: 'Quality Assured' },
              { key: 'bulk', icon: Package, t: 'Bulk Order Available' },
            ].map((it) => (
              <div key={it.key} className="bg-blue-50 rounded p-3 flex items-center gap-2">
                <it.icon className="w-5 h-5 text-[#2c54b2] shrink-0" />
                <span className="text-xs font-medium text-gray-800">{it.t}</span>
              </div>
            ))}
          </div>
        </div>

        <aside className="lg:sticky lg:top-32 h-fit">
          <div className="bg-white border border-gray-200 rounded-md p-4">
            <div className="text-xs text-gray-500 mb-1">Contact Vendor</div>
            {supplier && (
              <>
                <div className="flex items-center gap-3 mb-3">
                  {supplier.logo_url ? (
                    <img src={supplier.logo_url} alt={supplier.name} className="w-12 h-12 rounded-md object-cover bg-gray-100 border border-gray-200" />
                  ) : (
                    <div className="w-12 h-12 rounded-md bg-gradient-to-br from-[#2c54b2] to-[#1e3a8a] text-white flex items-center justify-center font-bold">{supplier.logo}</div>
                  )}
                  <div className="min-w-0">
                    <Link to={`/vendor/${supplier.slug || supplier.id}`} className="font-semibold text-gray-900 hover:text-[#2c54b2] truncate block">{supplier.name}</Link>
                    <div className="flex items-center gap-1 text-xs text-gray-600"><MapPin className="w-3 h-3" /> {supplier.city}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs mb-3">
                  <ShieldCheck className="w-4 h-4 text-green-600" />
                  <span className="font-medium text-green-700">{supplier.gst}</span>
                  <span className="text-gray-400">•</span>
                  <span className="text-gray-600">Since {supplier.established}</span>
                </div>
              </>
            )}
            <Button onClick={() => setEnquiryOpen(true)} className="w-full bg-[#ee5e23] hover:bg-[#d24e15] text-white h-11">
              <MessageSquare className="w-4 h-4 mr-2" /> Send Enquiry
            </Button>
            <Button variant="outline" className="w-full mt-2 border-[#2c54b2] text-[#2c54b2] hover:bg-[#2c54b2] hover:text-white h-11">
              <Phone className="w-4 h-4 mr-2" /> Call Now
            </Button>
            <div className="mt-4 pt-4 border-t border-gray-200 space-y-2 text-xs text-gray-700">
              {['Verified GST Certificate', 'Trusted by 5000+ Buyers', 'Response within 24 hours'].map((t) => (
                <div key={t} className="flex items-center gap-2"><Check className="w-3.5 h-3.5 text-green-600" /> {t}</div>
              ))}
            </div>
          </div>
        </aside>
      </div>

      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 py-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Explore Similar Products</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {related.slice(0, 4).map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 pb-10">
        <ReviewsList targetType="product" targetId={product.id} targetName={product.name} />
      </section>

      <Footer />
      <EnquiryModal open={enquiryOpen} onOpenChange={setEnquiryOpen} product={product} />
    </div>
  );
};

export default ProductDetailPage;
